import React from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { TypeKeys } from '@common/react/store/VideoChat';
import { ChatRoomType } from '@common/react/components/Chat/Chat';
import { RequestButton } from '@common/react/components/Core/LoadingButton/LoadingButton';
import { useApplicationContext } from '@common/react/components/Core/Application/Application';
import { BaseUser, BaseUserNotificationSettings } from '@common/react/objects/BaseUser';
import { useChatSettingsProviderContext } from '@common/react/components/Chat/ChatSettingsProvider';
import { useModal } from '@common/react/components/Modal/ModalContextProvider';

const shortBeeps = require('@common/react/audio/short-beeps.ogg');

interface VideoChatButtonProps {
	roomId: string;
	roomName: string;
	requestType?: string;
	notifyUserIds: Array<number>;
	avatar?: string;
	chatId?: number;
	support?: boolean;
	onClick?: () => void;
	roomType?: ChatRoomType;
	className?: string;
	disabled?: boolean;
	title?: string;
	children?: React.ReactNode;
}

const ChatRoomTypeIcons = {
	[ChatRoomType.Video]: 'video-camera',
	[ChatRoomType.Audio]: 'phone-square',
};

export const LastMessage = ({ message }) => <div className="chat-list__item-last-message">
	<i className={`fa fa-${ChatRoomTypeIcons[message.chatRoomType]}`} style={{ color: 'red' }} />
</div>;

const VideoChatButton: React.FC<VideoChatButtonProps> = (props) => {
	const context = useChatSettingsProviderContext(false);
	const {
		support,
		roomId,
		roomName,
		notifyUserIds,
		requestType = context?.state?.requests?.videoChat ?? 'videoChat',
		chatId,
		onClick,
		avatar,
		roomType = ChatRoomType.Video,
		className = 'video-button btn btn-default btn-circle',
		disabled,
		title,
	} = props;
	const { openErrorMessage } = useModal();
	const dispatch = useDispatch();
	const isChatting = useSelector((state: any) => state.videoChat.isChatting, shallowEqual);
	const { getUser } = useApplicationContext();
	const user = getUser<BaseUser & BaseUserNotificationSettings>();

	const handleSuccess = (res) => {
		dispatch({
			type: TypeKeys.UPDATE,
			data: {
				token: res.token,
				roomIdentifier: res.roomIdentifier,
				roomName,
				avatar,
				isSupport: support ?? false,
				hasError: false,
				chatId,
				preventCameraStart: roomType === ChatRoomType.Audio,
				visibility: true,
			},
		});
	};

	return <RequestButton<{token: string, roomIdentifier: string}>
		requestType={requestType}
		requestProps={{
			roomName: roomId,
			chatId,
			notifyUserIds,
			chatRoomType: roomType,
		}}
		beforeAction={(action, event) => {
			event.preventDefault();
			action();
			onClick && onClick();
			return false;
		}}
		title={title}
		onSuccess={({ res }) => handleSuccess(res)}
		onError={(e) => {
			openErrorMessage && openErrorMessage(e);
			dispatch({ type: TypeKeys.CLEAR });
			if (user?.enableSounds) {
				try {
					new Audio(shortBeeps).play();
				} catch (e) {
					console.log(e);
				}
			}
		}}
		disabled={disabled || isChatting}
		className={className}
	>
		{props.children ?? <i className={`fa fa-${ChatRoomTypeIcons[roomType]}`} />}
	</RequestButton>;
};

export default VideoChatButton;
